import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faXmark,
  faCircle,
  faHourglassHalf,
  faPhone,
  faTrashCan,
  faRotate,
  faTriangleExclamation,
  faArrowUpRightFromSquare,
} from '@fortawesome/free-solid-svg-icons';
import './DemoFollowUp.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });
const ICON = { done: faCheck, bad: faXmark, current: faHourglassHalf, todo: faCircle };

/* Demo profile › Follow-up: from the free class to paying, worked out from the data,
   plus your own "Contacted" notes (WhatsApp calls / messages). */
const DemoFollowUp = ({ studentId }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await axios.get(`${API}/api/admin/studentprofile/demoflow/${studentId}`, auth());
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'We couldn’t load the follow-up.');
    }
  }, [studentId]);

  useEffect(() => {
    load();
  }, [load]);

  const addContact = async (e) => {
    e.preventDefault();
    setBusy(true);
    setSaveError('');
    try {
      await axios.post(`${API}/api/admin/studentprofile/democontact/${studentId}`, { note }, auth());
      setNote('');
      await load();
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Couldn’t save it.');
    } finally {
      setBusy(false);
    }
  };

  const removeContact = async (id) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await axios.delete(`${API}/api/admin/studentprofile/democontact/${id}`, auth());
      await load();
    } catch {
      /* keep the list as it is */
    }
  };

  if (error && !data) {
    return (
      <section className="rmadf rmasp-card">
        <p className="rmadf-error">
          <FontAwesomeIcon icon={faTriangleExclamation} /> {error}{' '}
          <button type="button" className="rmadf-link" onClick={load}>
            <FontAwesomeIcon icon={faRotate} /> Try again
          </button>
        </p>
      </section>
    );
  }
  if (!data) return <section className="rmadf rmasp-card" aria-busy="true"><div className="rmadf-skel" /></section>;

  const next = data.steps.find((s) => s.state === 'current');

  return (
    <section className="rmadf rmasp-card">
      <div className="rmadf-head">
        <h2 className="rmasp-h2">Follow-up</h2>
        {data.convertedTo && (
          <Link className="rmasp-btn" to={`/Admin/studentprofile/${data.convertedTo.id}`}>
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} /> Open {data.convertedTo.status === 'pending' ? 'pending' : data.convertedTo.status === 'stopped' ? 'stopped' : 'paid'} profile
          </Link>
        )}
      </div>
      {next && (
        <p className="rmadf-next">
          <b>Next:</b> {next.label}
          {next.detail ? ` · ${next.detail}` : ''}
        </p>
      )}

      <ol className="rmadf-steps">
        {data.steps.map((s) => (
          <li key={s.key} className={`is-${s.state}`}>
            <span className="rmadf-dot" aria-hidden="true">
              <FontAwesomeIcon icon={ICON[s.state] || faCircle} />
            </span>
            <div>
              <b>{s.label}</b>
              {(s.detail || s.at) && <small>{[s.detail, s.at].filter(Boolean).join(' · ')}</small>}
            </div>
          </li>
        ))}
      </ol>

      <div className="rmadf-contacts">
        <h3>
          <FontAwesomeIcon icon={faPhone} /> Contacted
        </h3>
        {data.contactsReady ? (
          <>
            <form className="rmadf-add" onSubmit={addContact}>
              <input
                type="text"
                maxLength={500}
                placeholder="What did they say? e.g. Will pay on the 1st, wants Tuesday 5:30"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={busy}
              />
              <button type="submit" className="rmasp-btn" disabled={busy}>
                {busy ? 'Saving…' : 'Log contact'}
              </button>
            </form>
            {saveError && <p className="rmadf-error">{saveError}</p>}
            {data.contacts.length > 0 ? (
              <ul className="rmadf-list">
                {data.contacts.map((c) => (
                  <li key={c.id}>
                    <span>
                      <small>{c.at}</small>
                      {c.note || <i>Contacted</i>}
                    </span>
                    <button type="button" onClick={() => removeContact(c.id)} aria-label="Delete note">
                      <FontAwesomeIcon icon={faTrashCan} />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="rmadf-dim">No contact logged yet.</p>
            )}
          </>
        ) : (
          <p className="rmadf-dim">Run DemoFollowUp.sql on the server to log your calls here.</p>
        )}
      </div>
    </section>
  );
};

export default DemoFollowUp;
